import {LineItemModel} from '../../../../../../domain/models/LineItems/LineItem.model';
import {DescriptionLineModel} from '../../../../../../domain/models/LineItems/DescriptionLine.model';
import {MAX_DESCRIPTION_LINE_LENGTH, MAX_MODIFIER_LENGTH, ModifierDetails} from '../../../../../constants';
import {
  SubscriptionFrequency,
  SubscriptionSettingsModel,
} from '../../../../../../domain/models/LineItems/SubscriptionSettings.model';
import {TFunction} from '@wix/yoshi-flow-editor';
import {ModifierModel} from '../../../../../../domain/models/LineItems/Modifier.model';

const toDescriptionLineString = (descriptionLine: DescriptionLineModel): string => {
  if (!descriptionLine.title) {
    return trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH);
  }
  return `${descriptionLine.title}: ${trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH)}`;
};

export const mapLineItemToDetails = (
  lineItem: LineItemModel,
  t: any
): {
  descriptionLines?: string[];
  groups?: {descriptionLines: string[]; groupName?: string}[];
} => {
  const options: string[] = lineItem.attributes.descriptionLines.map(toDescriptionLineString);
  const modifierGroups = lineItem.modifierGroups;
  const hasOnlyOneSimpleGroup =
    modifierGroups?.length === 1 && !modifierGroups[0].name && modifierGroups[0].modifiers.length === 1;

  let groups;
  if (hasOnlyOneSimpleGroup && options.length === 0) {
    options.push(toModifierString(lineItem.modifierGroups[0].modifiers[0], t));
  } else {
    groups = lineItem.modifierGroups.map((group) => {
      return {
        groupName: group.name,
        descriptionLines: group.modifiers.map((modifier) => toModifierString(modifier, t)),
      };
    });
  }

  return {descriptionLines: options, groups};
};

const toModifierString = (modifier: ModifierModel, t: any): string => {
  let modifierName;
  if (!modifier.details) {
    modifierName = trimToMaxLength(modifier.label, MAX_MODIFIER_LENGTH);
  } else {
    modifierName = trimToMaxLength(`${modifier.label}: ${modifier.details}`, MAX_MODIFIER_LENGTH);
  }

  const modifierDetails = () => {
    if (modifier.quantity && modifier.price) {
      return ModifierDetails.QuantityAndPrice;
    }
    if (modifier.quantity) {
      return ModifierDetails.Quantity;
    }
    if (modifier.price) {
      return ModifierDetails.Price;
    }
    return ModifierDetails.Other;
  };

  return t('cart.lineItems.modifierDetails', {
    modifierDetails: modifierDetails(),
    quantity: modifier.quantity || undefined,
    price: modifier.price ? modifier.price.formattedConvertedAmount : undefined,
    modifierName,
  });
};

export const trimToMaxLength = (value: string, maxLength?: number): string => {
  if (!maxLength || value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 3)}...`;
};

export function getSubscriptionDuration(subscriptionSettings: SubscriptionSettingsModel, t: TFunction): string {
  if (subscriptionSettings.autoRenewal) {
    return t('cart.pricingPlans.duration.label', {
      number: undefined,
      frequency: t('cart.page_plan_autorenew'),
    });
  }

  const cycles = subscriptionSettings.billingCycles * subscriptionSettings.interval;
  const frequency = getFrequencyText({
    frequency: subscriptionSettings.frequency,
    cycles,
    t,
  });

  return t('cart.pricingPlans.duration.label', {
    number: subscriptionSettings.interval > 0 ? cycles : undefined,
    frequency,
  });
}

export function getFrequencyText({
  frequency,
  cycles,
  t,
}: {
  frequency: SubscriptionFrequency;
  cycles: number;
  t: TFunction;
}) {
  switch (frequency) {
    case SubscriptionFrequency.DAY: {
      return cycles > 1 ? t('cart.pricingPlans.frequency.days.plural') : t('cart.pricingPlans.frequency.day.singular');
    }
    case SubscriptionFrequency.WEEK: {
      return cycles > 1
        ? t('cart.pricingPlans.frequency.weeks.plural')
        : t('cart.pricingPlans.frequency.week.singular');
    }
    case SubscriptionFrequency.MONTH: {
      return cycles > 1
        ? t('cart.pricingPlans.frequency.months.plural')
        : t('cart.pricingPlans.frequency.month.singular');
    }
    case SubscriptionFrequency.YEAR: {
      return cycles > 1
        ? t('cart.pricingPlans.frequency.years.plural')
        : t('cart.pricingPlans.frequency.year.singular');
    }
    case SubscriptionFrequency.UNDEFINED: {
      return t('cart.page_plan_untilCanceled');
    }
  }
}
